import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { ChevronLeft } from "lucide-react";
import { PageHeader } from "@/components/app-shell";
import { CalcShell, Field, NumberInput, ResultCard, fmt } from "@/components/calculators/CalcShell";
import { AddToQuoteButton } from "@/components/calculators/AddToQuoteButton";
import { linesFromElectricianNote } from "@/components/calculators/lineMappers";
import { checkBreaker } from "@/domain/calculators";

export const Route = createFileRoute("/_authenticated/calculations/breaker")({
  component: BreakerPage,
});

function BreakerPage() {
  const [Ib, setIb] = useState<number | "">("");
  const [Iz, setIz] = useState<number | "">("");
  const [In, setIn] = useState<number | "">("");

  const result = useMemo(() => {
    if (Ib === "" || Iz === "") return null;
    try {
      return checkBreaker({ Ib, Iz, In: In === "" ? undefined : In });
    } catch (e) {
      return { error: (e as Error).message };
    }
  }, [Ib, Iz, In]);

  return (
    <>
      <Link to="/calculations" className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> Beregninger
      </Link>
      <PageHeader title="Vern" subtitle="Kontroll av Ib ≤ In ≤ Iz (NEK 400 §433.1)." />
      <CalcShell
        title="Inndata"
        description="La In stå tomt for å få foreslått standard vernstørrelse."
        result={
          result && "error" in result ? (
            <div className="card p-5 text-sm text-rose-600">{result.error}</div>
          ) : result ? (
            <>
            <ResultCard
              title="Kontroll"
              ok={result.ok}
              rows={[
                { label: "Ib (belastning)",  value: `${fmt(result.Ib, 1)} A` },
                { label: "In (vern)",        value: `${fmt(result.In, 1)} A${result.suggested ? " (forslag)" : ""}`, strong: true },
                { label: "Iz (kabel)",       value: `${fmt(result.Iz, 1)} A` },
                { label: "Ib ≤ In",          value: result.ibLeqIn ? "✓" : "✗" },
                { label: "In ≤ Iz",          value: result.inLeqIz ? "✓" : "✗" },
              ]}
              note={result.message}
            />
            <div className="mt-3 flex justify-end">
              <AddToQuoteButton sourceLabel={`Vernberegning`} lines={linesFromElectricianNote(`Vern In: valgt størrelse`)} />
            </div>
          </>
          ) : <div className="card p-5 text-sm text-muted-foreground">Fyll inn Ib og Iz.</div>
        }
      >
        <Field label="Belastningsstrøm Ib" unit="A"><NumberInput value={Ib} onChange={setIb} /></Field>
        <Field label="Kabelens Iz" unit="A"><NumberInput value={Iz} onChange={setIz} /></Field>
        <Field label="Vern In (valgfritt)" unit="A"><NumberInput value={In} onChange={setIn} /></Field>
      </CalcShell>
    </>
  );
}
